import { useCallback, useSyncExternalStore } from 'react';

const stores = new Map();

const getStore = (groupId) => {
    if (!stores.has(groupId)) {
        stores.set(groupId, { id: null, listeners: new Set() });
    }
    return stores.get(groupId);
};

/**
 * Single-expansion coordinator shared across every accordion list in the
 * deck panes. All callers using the same `groupId` (default: a single
 * global bucket) share one expanded id — opening a row anywhere closes
 * whatever was open before, including rows in a different pane.
 */
export function useExclusiveExpansion(groupId = 'global') {
    const store = getStore(groupId);

    const subscribe = useCallback(
        (cb) => {
            store.listeners.add(cb);
            return () => store.listeners.delete(cb);
        },
        [store]
    );
    const getSnapshot = useCallback(() => store.id, [store]);
    const expandedId = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    const setExpandedId = useCallback(
        (next) => {
            const nextId = typeof next === 'function' ? next(store.id) : next;
            if (nextId === store.id) return;
            store.id = nextId;
            store.listeners.forEach((cb) => cb());
        },
        [store]
    );

    const toggle = useCallback(
        (id) => {
            setExpandedId((prev) => (prev === id ? null : id));
        },
        [setExpandedId]
    );

    return { expandedId, setExpandedId, toggle };
}
